'use client';
import { ReactElement, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { type ID, Product } from '@nutralab/api';
import { Button, Flex, Grid, Heading } from '@nutralab/ui';

import { classNames } from '@m1work/shared-utils';

import { ProductCard } from '../ProductCard';

import { CategoryFilter } from './_components/CategoryFilter';
import styles from './ProductList.module.scss';
import { useCatalogView } from './useCatalogView';
import { useCompactHeader } from './useCompactHeader';

import { useSiteConfig } from '@/app/_providers/SiteConfigProvider';
import { AppLayer } from '@/shared/constants';
import { getCartKey, getCatalogItemPath, getCurrencySign, getImageUrl } from '@/shared/utils';

const PRODUCTS_PER_PAGE = 9;

function uniqueById(items: Product[]): Product[] {
    const seen = new Set<ID>();

    return items.filter((item) => {
        if (seen.has(item.id)) {
            return false;
        }
        seen.add(item.id);
        return true;
    });
}

type ProductListProps = React.HTMLAttributes<HTMLDivElement>;

/** The home page catalog: products by category with a category filter and "show more". */
export function ProductList(props: ProductListProps): ReactElement {
    const { t } = useTranslation();
    const { products, categories } = useSiteConfig();

    const rootRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const filterRef = useRef<HTMLDivElement>(null);
    const { categoryId, isExpanded, setView } = useCatalogView();
    // A category saved earlier may be gone from the catalog by now.
    const selectedCategoryId = categories.some((c) => c.id === categoryId) ? categoryId : null;

    useCompactHeader(rootRef, sentinelRef, filterRef);

    const productMap = useMemo(() => {
        const map: Record<ID, Product[]> = {};
        products.forEach((g) => {
            map[g.category_id] = g.items;
        });
        return map;
    }, [products]);

    const productsToRender = useMemo<Product[]>(() => {
        const list = uniqueById(
            selectedCategoryId === null ? Object.values(productMap).flat() : (productMap[selectedCategoryId] ?? []),
        );
        return list.sort((a, b) => {
            if (a.priority === null && b.priority === null) {
                return 0;
            }
            if (a.priority === null) {
                return 1;
            }
            if (b.priority === null) {
                return -1;
            }
            return a.priority - b.priority;
        });
    }, [selectedCategoryId, productMap]);

    const visibleProducts = isExpanded ? productsToRender : productsToRender.slice(0, PRODUCTS_PER_PAGE);
    const reserveOldPrice = visibleProducts.some((p) => p.old_price);
    const reserveTimer = visibleProducts.some((p) => p.old_price && p.price_expires_at);

    const handleSelectCategory = (id: ID | null): void => {
        if (selectedCategoryId !== id) {
            setView({ categoryId: id, isExpanded: false });
        }

        const sentinel = sentinelRef.current;
        if (sentinel && !window.matchMedia('(min-width: 768px)').matches) {
            const navbarHeight =
                parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--mobile-navbar-height')) || 0;
            window.scrollTo({
                top: sentinel.getBoundingClientRect().top + window.scrollY - navbarHeight,
                behavior: 'smooth',
            });
            return;
        }

        rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div id="products" ref={rootRef} {...props}>
            <Heading level={1} className="mb-4 md:mb-20" size="2xl" align="center" weight="semibold">
                {t('landing.main-product-list.title')}
            </Heading>

            {categories.length > 0 && (
                <>
                    <div ref={sentinelRef} />
                    <div
                        ref={filterRef}
                        className={classNames(styles['filter-sticky'], 'mb-8')}
                        style={{ zIndex: AppLayer.FILTER }}
                    >
                        <CategoryFilter
                            categories={categories}
                            selectedId={selectedCategoryId}
                            onSelect={handleSelectCategory}
                            allLabel={t('landing.main-product-list.all-categories')}
                            ariaLabel={t('landing.main-product-list.title')}
                        />
                    </div>
                </>
            )}

            <Grid columns={{ base: 1, md: 2, xl: 3 }} gap={8} className="mb-16">
                {visibleProducts.map((p) => (
                    <ProductCard
                        key={getCartKey(p)}
                        productId={p.id}
                        productType={p.type}
                        href={getCatalogItemPath(p)}
                        name={p.name}
                        description={p.description}
                        tags={p.tags}
                        imageSrc={getImageUrl(p.main_image)}
                        price={p.price.amount}
                        oldPrice={p.old_price?.amount ?? null}
                        priceExpiresAt={p.price_expires_at}
                        reserveOldPrice={reserveOldPrice}
                        reserveTimer={reserveTimer}
                        currencySign={getCurrencySign(p.price.currency)}
                        cardBorderColor={p.card_border_color}
                        isUndocumented={p.is_undocumented}
                    />
                ))}
            </Grid>

            {productsToRender.length > PRODUCTS_PER_PAGE && (
                <Flex justify="center">
                    <Button
                        variant="outlined"
                        size="lg"
                        onClick={() => setView({ categoryId: selectedCategoryId, isExpanded: !isExpanded })}
                    >
                        {isExpanded ? t('landing.main-product-list.hide') : t('landing.main-product-list.show-all')}
                    </Button>
                </Flex>
            )}
        </div>
    );
}
